import React, { useState, useCallback, useReducer, useMemo } from 'react';
import { 
    Box, 
    Button, 
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Upload as UploadIcon } from '@mui/icons-material';
import { provider, UPLOAD, CREATE, UPDATE } from '../../_utils';
import { TableElement, CardElement, EmptyState } from '../../_Components/Elements';
import StorageIcon from '@mui/icons-material/Storage';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';
import { UploadFileModal, EditDatabaseModal } from '../components';

const Databases = ({ projectId, formValues, queues, change }) => {
    // Reducer для управления состоянием модального окна загрузки
    const uploadModalReducer = (state, action) => {
        switch (action.type) {
            case 'SET_MODAL_OPEN':
                return { ...state, isOpen: action.payload };
            case 'SET_FILE':
                return { ...state, selectedFile: action.payload };
            case 'SET_UPLOAD_STATUS':
                return { ...state, uploadStatus: action.payload };
            case 'SET_DRAG_OVER':
                return { ...state, isDragOver: action.payload };
            case 'SET_CURRENT_STEP':
                return { ...state, currentStep: action.payload };
            case 'SET_PARSED_DATA':
                return { ...state, parsedData: action.payload };
            case 'SET_PROCESSING':
                return { ...state, isProcessing: action.payload };
            case 'SET_SAVING':
                return { ...state, isSaving: action.payload };
            case 'SET_FIELD_MAPPING':
                return { ...state, fieldMapping: action.payload };
            case 'SET_PHONE_FIELDS':
                return { ...state, phoneFields: action.payload };
            case 'SET_SELECTED_CARD':
                return { ...state, selectedCard: action.payload };
            case 'RESET_MODAL':
                return {
                    isOpen: false,
                    selectedFile: null,
                    uploadStatus: null,
                    isDragOver: false,
                    currentStep: 1,
                    parsedData: null,
                    isProcessing: false,
                    isSaving: false,
                    fieldMapping: {},
                    phoneFields: {},
                    selectedCard: null
                };
            default:
                return state;
        }
    };

    const [uploadModalState, uploadModalDispatch] = useReducer(uploadModalReducer, {
        isOpen: false,
        selectedFile: null,
        uploadStatus: null,
        isDragOver: false,
        currentStep: 1,
        parsedData: null,
        isProcessing: false,
        isSaving: false,
        fieldMapping: {},
        phoneFields: {},
        selectedCard: null
    });

    // Состояние для модального окна редактирования
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingDatabase, setEditingDatabase] = useState(null);
    
    // Состояние для формы редактирования
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [isCheckingDatabase, setIsCheckingDatabase] = useState(false);
    const [isLoadingDatabase, setIsLoadingDatabase] = useState(false);
    const [checkResult, setCheckResult] = useState(null);
    
    // Состояние для диалога подтверждения
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const validateFile = useCallback((file) => {
        if (!file) {
            return { isValid: false, error: 'Файл не выбран' };
        }
        
        // Проверяем тип файла
        const allowedTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'
        ];
        
        if (!allowedTypes.includes(file.type)) {
            return { isValid: false, error: 'Поддерживаются только файлы Excel (.xlsx, .xls)' };
        }
        
        // Проверяем размер файла (максимум 10 MB)
        const maxSize = 10 * 1024 * 1024; // 10 MB
        if (file.size > maxSize) {
            return { 
                isValid: false, 
                error: `Размер файла превышает 10 MB. Текущий размер: ${(file.size / 1024 / 1024).toFixed(2)} MB` 
            };
        }
        
        return { isValid: true };
    }, []);

    const handleFileSelect = useCallback((event) => {
        const file = event.target.files[0];
        const validation = validateFile(file);
        
        if (validation.isValid) {
            uploadModalDispatch({ type: 'SET_FILE', payload: file });
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: null });
        } else {
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'error', message: validation.error } });
            uploadModalDispatch({ type: 'SET_FILE', payload: null });
        }
    }, [validateFile]);

    // Открытие модального окна редактирования
    const handleEditDatabase = useCallback((database) => {
        if(!database){
            setEditingDatabase({});
        }else{
            setEditingDatabase(database);
        }
        setIsEditModalOpen(true);
    }, []);

    // Закрытие модального окна редактирования
    const handleCloseEditModal = useCallback(() => {
        setIsEditModalOpen(false);
        setEditingDatabase(null);
        setIsSavingEdit(false);
    }, []);

    const showConfirmDialog = useCallback((title, message, onConfirm) => {
        setConfirmDialog({
            isOpen: true,
            title,
            message,
            onConfirm
        });
    }, []);

    const closeConfirmDialog = useCallback(() => {
        setConfirmDialog({
            isOpen: false,
            title: '',
            message: '',
            onConfirm: null
        });
    }, []);

    // Мемоизация стабильных значений для зависимостей
    const currentStep = useMemo(() => uploadModalState.currentStep, [uploadModalState.currentStep]);
    const fieldMapping = useMemo(() => uploadModalState.fieldMapping, [uploadModalState.fieldMapping]);
    const parsedData = useMemo(() => uploadModalState.parsedData, [uploadModalState.parsedData]);
    const selectedCard = useMemo(() => uploadModalState.selectedCard, [uploadModalState.selectedCard]);

    const handleConfirmAction = useCallback(() => {
        if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
        }
        closeConfirmDialog();
    }, [confirmDialog, closeConfirmDialog]);



    // Сохранение изменений базы данных
    const handleSaveEdit = useCallback(async (formData) => {
        if (!formData || !formData.name || !formData.queue) {
            return;
        }

        const payload = {
            ...formData,
            project: projectId,
            type: formData.type || 'MongoDB'
        };
        if (payload.query_mongo && formData.project && formData.project !== projectId) {
            payload.query_mongo = {
                ...payload.query_mongo,
                project: formData.project
            };
        }

        setIsSavingEdit(true);
        try {
            const currentBases = (formValues.base || []).filter((db) => db && db._id);
            if(editingDatabase && editingDatabase._id){
                const result = await provider(UPDATE, '/bases', { id: editingDatabase._id, data: payload });
                if (result && result.status === 200 && result.data) {
                    const next = currentBases.map((db) => String(db._id) === String(editingDatabase._id) ? result.data : db);
                    if (change) {
                        change('base', next);
                    }
                    handleCloseEditModal();
                }
            }else{
                const result = await provider(CREATE, '/bases', {data: payload});
                if (result && result.status === 200 && result.data) {
                    const next = [...currentBases, result.data];
                    if (change) {
                        change('base', next);
                    }
                    await provider(UPDATE, '/projects', {
                        id: projectId,
                        data: { base: next.map((db) => db._id) }
                    });
                    handleCloseEditModal();
                }
            }
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            uploadModalDispatch({ 
                type: 'SET_UPLOAD_STATUS', 
                payload: { 
                    status: 'error', 
                    message: `Ошибка при сохранении: ${error.message || 'Неизвестная ошибка'}` 
                } 
            });
        } finally {
            setIsSavingEdit(false);
        }
    }, [editingDatabase, projectId, formValues, change, handleCloseEditModal]);

    // Проверка базы данных
    const handleCheckDatabase = useCallback(async (formData) => {
        if (!editingDatabase) {
            return;
        }

        // Если передали null, очищаем результат
        if (formData === null) {
            setCheckResult(null);
            return;
        }

        setIsCheckingDatabase(true);
        try {
            // Здесь будет API вызов для проверки базы данных
            const result = await provider(CREATE, `/import/check`, {data: formData});
            // Сохраняем результат проверки
            if (result && result.data) {
                setCheckResult(result.data.data);
            } else {
                setCheckResult(null);
            }
        } catch (error) {
            console.error('Ошибка при проверке:', error);
            setCheckResult(null);
            uploadModalDispatch({ 
                type: 'SET_UPLOAD_STATUS', 
                payload: { 
                    status: 'error', 
                    message: `Ошибка при проверке: ${error.message || 'Неизвестная ошибка'}` 
                } 
            });
        } finally {
            setIsCheckingDatabase(false);
        }
    }, [editingDatabase]);

    // Загрузка базы данных
    const handleLoadDatabase = useCallback(async (formData) => {
        if (!editingDatabase) {
            return;
        }

        setIsLoadingDatabase(true);
        try {
                    // Здесь будет API вызов для загрузки базы данных
        const result = await provider(CREATE, `/import/load`, {data: formData});
        if (result && result.status === 200) {
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'success', message: 'База данных успешно загружена' } });
        }
            
            // Пока что просто логируем
        } catch (error) {
            console.error('Ошибка при загрузке:', error);
            uploadModalDispatch({ 
                type: 'SET_UPLOAD_STATUS', 
                payload: { 
                    status: 'error', 
                    message: `Ошибка при загрузке: ${error.message || 'Неизвестная ошибка'}` 
                } 
            });
        } finally {
            setIsLoadingDatabase(false);
        }
    }, [editingDatabase]);

    // Переход к следующему шагу
    const handleNextStep = useCallback(() => {
        if (currentStep < 3) {
            uploadModalDispatch({ type: 'SET_CURRENT_STEP', payload: currentStep + 1 });
        }
    }, [currentStep]);

    /**
     * Очищает телефонный номер от всех символов кроме цифр
     */
    const cleanPhoneNumber = useCallback((phoneValue) => {
        if (!phoneValue) return '';
        return phoneValue.toString().replace(/\D/g, '');
    }, []);

    /**
     * Преобразует данные в требуемый формат для отправки на сервер
     */
    const transformDataForServer = useCallback((parsedData, fieldMapping, phoneFields, selectedCard) => {
        if (!parsedData?.data?.rows || !fieldMapping || !selectedCard) {
            return null;
        }

        const transformedData = [];
        
        parsedData.data.rows.forEach((row, rowIndex) => {
            const fields = {};
            const phones = [];
            
            // Обрабатываем каждую строку данных
            Object.keys(fieldMapping).forEach(headerIndex => {
                const field = fieldMapping[headerIndex];
                const value = row.data[headerIndex];
                
                if (value !== undefined && value !== null && value !== '') {
                    // Определяем, является ли поле телефонным
                    const isPhoneField = phoneFields[headerIndex];
                    
                    // Очищаем значение от лишних символов, если это телефонное поле
                    let cleanedValue = value;
                    if (isPhoneField) {
                        // Убираем все символы кроме цифр
                        cleanedValue = cleanPhoneNumber(value);
                        
                        // Если после очистки остались только цифры, добавляем в массив phones
                        if (cleanedValue.length > 0) {
                            phones.push(cleanedValue);
                        }
                    }
                    
                    // Добавляем поле в объект fields
                    // Используем value как приоритет, затем name, затем fallback
                    const fieldKey = field.value || field.name || `field_${headerIndex}`;
                    fields[fieldKey] = cleanedValue;
                }
            });
            
            // Добавляем запись только если есть поля
            if (Object.keys(fields).length > 0) {
                transformedData.push({
                    fields,
                    phones: phones.length > 0 ? phones : [],
                    project: selectedCard._id
                });
            }
        });
        
        return transformedData;
    }, [cleanPhoneNumber]);

    // Мемоизация преобразованных данных для оптимизации
    const memoizedTransformData = useMemo(() => {
        return transformDataForServer(
            uploadModalState.parsedData, 
            uploadModalState.fieldMapping, 
            uploadModalState.phoneFields, 
            uploadModalState.selectedCard
        );
    }, [
        uploadModalState.parsedData, 
        uploadModalState.fieldMapping, 
        uploadModalState.phoneFields, 
        uploadModalState.selectedCard, 
        transformDataForServer
    ]);

    // Мемоизация данных для сохранения
    const saveData = useMemo(() => {
        if (!uploadModalState.parsedData || !uploadModalState.selectedCard || !uploadModalState.fieldMapping || !uploadModalState.phoneFields || !uploadModalState.selectedFile) {
            return null;
        }
        
        return {
            projectId,
            cardId: uploadModalState.selectedCard._id,
            cardName: uploadModalState.selectedCard.name,
            transformedData: memoizedTransformData,
            originalData: {
                fileName: uploadModalState.parsedData.file?.name || uploadModalState.selectedFile.name,
                fileSize: uploadModalState.parsedData.file?.size || uploadModalState.selectedFile.size,
                metadata: uploadModalState.parsedData.stats,
                headers: uploadModalState.parsedData.data?.headers,
                fieldMapping: uploadModalState.fieldMapping,
                phoneFields: uploadModalState.phoneFields
            }
        };
    }, [
        projectId,
        memoizedTransformData,
        uploadModalState.parsedData,
        uploadModalState.selectedCard,
        uploadModalState.fieldMapping,
        uploadModalState.phoneFields,
        uploadModalState.selectedFile
    ]);

    /**
     * Автоматически сопоставляет заголовки с полями по совпадению названий
     */
    const autoMapFields = useCallback((headers, availableFields) => {
        if (!headers || !availableFields || !Array.isArray(availableFields)) return {};
        
        const mapping = {};
        headers.forEach(header => {
            // Ищем поле по точному совпадению или частичному совпадению
            const matchingField = availableFields.find(field => {
                const headerName = header.name.toLowerCase();
                const fieldLabel = (field.label || field.name || '').toLowerCase();
                
                // Точное совпадение
                if (headerName === fieldLabel) return true;
                
                // Частичное совпадение (заголовок содержит название поля или наоборот)
                if (headerName.includes(fieldLabel) || fieldLabel.includes(headerName)) return true;
                
                // Специальные случаи для телефонов
                if ((headerName.includes('телефон') || headerName.includes('phone') || headerName.includes('тел')) &&
                    (fieldLabel.includes('телефон') || fieldLabel.includes('phone') || fieldLabel.includes('тел'))) {
                    return true;
                }
                
                // Специальные случаи для email
                if ((headerName.includes('email') || headerName.includes('почта') || headerName.includes('mail')) &&
                    (fieldLabel.includes('email') || fieldLabel.includes('почта') || fieldLabel.includes('mail'))) {
                    return true;
                }
                
                return false;
            });
            
            if (matchingField) {
                // Сохраняем оригинальное поле из formValues.fields
                mapping[header.index.toString()] = matchingField;
            }
        });
        
        return mapping;
    }, []);

    /**
     * Шаг 1: Загружает и парсит выбранный xlsx файл
     * Использует UPLOAD константу и endpoint /import/check
     * Максимальный размер файла: 10 MB
     */
    const handleUploadAndParse = useCallback(async () => {
        if (!projectId) {
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'error', message: 'ID проекта не найден' } });
            return;
        }
        
        if (!uploadModalState.selectedFile) {
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'error', message: 'Пожалуйста, выберите файл для загрузки' } });
            return;
        }

        // Проверяем наличие токена авторизации
        const token = localStorage.getItem('token');
        if (!token) {
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'error', message: 'Токен авторизации не найден. Пожалуйста, войдите в систему.' } });
            return;
        }

        // Проверяем размер файла согласно API (максимум 10 MB)
        const maxFileSize = 10 * 1024 * 1024; // 10 MB в байтах
        if (uploadModalState.selectedFile.size > maxFileSize) {
            uploadModalDispatch({ 
                type: 'SET_UPLOAD_STATUS', 
                payload: { 
                    status: 'error', 
                    message: `Размер файла превышает 10 MB. Текущий размер: ${(uploadModalState.selectedFile.size / 1024 / 1024).toFixed(2)} MB` 
                } 
            });
            return;
        }

        // Дополнительная валидация файла
        const fileValidation = validateFile(uploadModalState.selectedFile);
        if (!fileValidation.isValid) {
            uploadModalDispatch({ 
                type: 'SET_UPLOAD_STATUS', 
                payload: { 
                    status: 'error', 
                    message: fileValidation.error 
                } 
            });
            return;
        }

        uploadModalDispatch({ type: 'SET_PROCESSING', payload: true });
        uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'info', message: 'Загрузка и парсинг файла...' } });

        try {
            // Проверяем, что файл действительно существует и валиден
            if (!uploadModalState.selectedFile || !(uploadModalState.selectedFile instanceof File)) {
                throw new Error('Некорректный файл для загрузки');
            }
            
            const formData = new FormData();
            formData.append('file', uploadModalState.selectedFile);

            const {data, status} = await provider(UPLOAD, '/xlsx/upload', { 
                data: formData
            });
            
            // Обрабатываем результат парсинга
            if (data && status === 200) {
                uploadModalDispatch({ type: 'SET_PARSED_DATA', payload: data });
                
                // Автоматически сопоставляем поля, если доступны formValues
                if (formValues && formValues.fields && Array.isArray(formValues.fields)) {
                    const autoMapping = autoMapFields(data.data?.headers, formValues.fields);
                    uploadModalDispatch({ type: 'SET_FIELD_MAPPING', payload: autoMapping });
                    
                    // Автоматически отмечаем телефонные поля
                    const phoneMapping = {};
                    Object.keys(autoMapping).forEach(headerIndex => {
                        const field = autoMapping[headerIndex];
                        if (field.label && (
                            field.label.toLowerCase().includes('телефон') || 
                            field.label.toLowerCase().includes('phone') || 
                            field.label.toLowerCase().includes('тел')
                        )) {
                            phoneMapping[headerIndex] = true;
                        }
                    });
                    uploadModalDispatch({ type: 'SET_PHONE_FIELDS', payload: phoneMapping });
                }
                
                uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'success', message: 'Файл успешно загружен и распарсен!' } });
                uploadModalDispatch({ type: 'SET_CURRENT_STEP', payload: 2 }); // Переходим ко второму шагу
            } else if (data && data.error) {
                // Обрабатываем ошибку от сервера
                throw new Error(data.error.message || data.error);
            } else {
                throw new Error('Неожиданный ответ от сервера');
            }
        } catch (error) {
            console.error('Ошибка загрузки и парсинга файла:', error);
            uploadModalDispatch({ 
                type: 'SET_UPLOAD_STATUS', 
                payload: { 
                    status: 'error', 
                    message: `Ошибка обработки: ${error.message || 'Неизвестная ошибка'}` 
                } 
            });
        } finally {
            uploadModalDispatch({ type: 'SET_PROCESSING', payload: false });
        }
    }, [projectId, uploadModalState.selectedFile, autoMapFields, formValues, validateFile]);

    /**
     * Шаг 3: Сохраняет результаты парсинга на сервер
     */
    const handleSaveResults = useCallback(async () => {
        if (!parsedData || !projectId) {
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'error', message: 'Нет данных для сохранения' } });
            return;
        }

        if (!selectedCard) {
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'error', message: 'Пожалуйста, выберите карточку проекта' } });
            return;
        }

        if (!fieldMapping || Object.keys(fieldMapping).length === 0) {
            uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'error', message: 'Пожалуйста, сопоставьте поля перед сохранением' } });
            return;
        }

        // Показываем диалог подтверждения
        showConfirmDialog(
            'Подтверждение сохранения',
            `Вы уверены, что хотите сохранить ${parsedData.data?.rows?.length || 0} записей в карточку "${selectedCard.name}"?`,
            async () => {
                uploadModalDispatch({ type: 'SET_SAVING', payload: true });
                uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'info', message: 'Сохранение результатов...' } });
                
                try {
                    // Используем мемоизированные данные для оптимизации
                    if (!saveData) {
                        throw new Error('Нет данных для сохранения. Проверьте маппинг полей.');
                    }

                    const result = await provider(CREATE, '/import/save', { data: saveData });

                    if (result && result.status === 200) {
                        uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'success', message: 'Данные успешно сохранены!' } });
                        
                        // Автоматически закрываем модальное окно через 2 секунды
                        setTimeout(() => {
                            uploadModalDispatch({ type: 'RESET_MODAL' });
                        }, 2000);
                    } else {
                        throw new Error('Ошибка при сохранении результатов');
                    }
                } catch (error) {
                    console.error('Ошибка сохранения результатов:', error);
                    uploadModalDispatch({ 
                        type: 'SET_UPLOAD_STATUS', 
                        payload: { 
                            status: 'error', 
                            message: `Ошибка сохранения: ${error.message || 'Неизвестная ошибка'}` 
                        } 
                    });
                } finally {
                    uploadModalDispatch({ type: 'SET_SAVING', payload: false });
                }
            }
        );
    }, [projectId, saveData, showConfirmDialog, parsedData, selectedCard, fieldMapping]);



    const handleCloseModal = useCallback(() => {
        uploadModalDispatch({ type: 'RESET_MODAL' });
    }, []);

    /**
     * Переход к предыдущему шагу
     */
    const goToPreviousStep = useCallback(() => {
        if (currentStep > 1) {
            uploadModalDispatch({ type: 'SET_CURRENT_STEP', payload: currentStep - 1 });
        }
    }, [currentStep]);


    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        uploadModalDispatch({ type: 'SET_DRAG_OVER', payload: true });
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        uploadModalDispatch({ type: 'SET_DRAG_OVER', payload: false });
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        uploadModalDispatch({ type: 'SET_DRAG_OVER', payload: false });
        
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            const validation = validateFile(file);
            
            if (validation.isValid) {
                uploadModalDispatch({ type: 'SET_FILE', payload: file });
                uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: null });
            } else {
                uploadModalDispatch({ type: 'SET_UPLOAD_STATUS', payload: { status: 'error', message: validation.error } });
                uploadModalDispatch({ type: 'SET_FILE', payload: null });
            }
        }
    }, [validateFile]);





    

    const bases = (formValues.base || []).filter((db) => db && db._id && db.name);

    return (
        <Box className="pc-fill" sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <CardElement
                fill
                title="Базы обзвона"
                action={(
                    <Box display="flex" gap={1}>
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<UploadIcon />}
                    onClick={() => {
                        uploadModalDispatch({ type: 'SET_MODAL_OPEN', payload: true });
                    }}
                    disabled={uploadModalState.isProcessing || uploadModalState.isSaving}
                >
                    {uploadModalState.isProcessing ? 'Обработка...' : 
                     uploadModalState.isSaving ? 'Сохранение...' : 'Загрузить базу'}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleEditDatabase()}
                    disabled={isSavingEdit}
                >
                    {isSavingEdit ? 'Сохранение...' : 'Добавить'}
                </Button>
                    </Box>
                )}
                content={
                    bases.length === 0 ? (
                        <EmptyState
                            icon={<StorageIcon fontSize="small" />}
                            title="Баз пока нет"
                            description="Загрузите файл с контактами — операторы получат их в работу по расписанию проекта."
                            action={
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<UploadIcon />}
                                    onClick={() => uploadModalDispatch({ type: 'SET_MODAL_OPEN', payload: true })}
                                >
                                    Загрузить базу
                                </Button>
                            }
                        />
                    ) : (
                        <div className="pc-panel-body">
                        <div className="pc-tblwrap">
                        <TableElement
                            header={[
                                { label: 'Название', value: 'name', width: 20 },
                                { label: 'Очередь', value: 'queue', width: 18 },
                                { label: 'Тип', value: 'type', width: 12 },
                                { label: 'URL', value: 'url', width: 20 },
                                { label: 'Загружено', value: 'load', width: 10 },
                                { label: 'Осталось', value: 'left', width: 10 },
                                { label: 'Действия', value: 'actions', width: 10 }
                            ]}
                            rows={bases.map((db, index) => ({
                                ...db,
                                type: db.type || 'MongoDB',
                                load: db.load != null ? db.load : '—',
                                left: db.left != null ? db.left : '—',
                                actions: (
                                    <Button
                                        style={{
                                            padding: '1px 3px',
                                            height: 20,
                                            width: 125,
                                            margin: 0,
                                        }}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleEditDatabase(db)}
                                        startIcon={<EditIcon  style={{fontSize: '0.75rem'}}/>}
                                        disabled={isSavingEdit}
                                    >
                                        {isSavingEdit ? 'Сохранение...' : 'Редактировать'}
                                    </Button>
                                )
                            }))}
                            edit={false}
                            remove={false}
                        />
                        </div>
                        </div>
                    )
                }
            />

            {/* Модальное окно для загрузки файла */}
            <UploadFileModal
                isOpen={uploadModalState.isOpen}
                onClose={handleCloseModal}
                currentStep={uploadModalState.currentStep}
                selectedFile={uploadModalState.selectedFile}
                parsedData={uploadModalState.parsedData}
                fieldMapping={uploadModalState.fieldMapping}
                phoneFields={uploadModalState.phoneFields}
                selectedCard={uploadModalState.selectedCard}
                isProcessing={uploadModalState.isProcessing}
                isSaving={uploadModalState.isSaving}
                uploadStatus={uploadModalState.uploadStatus}
                isDragOver={uploadModalState.isDragOver}
                onFileSelect={handleFileSelect}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onUploadAndParse={handleUploadAndParse}
                onFieldMappingChange={(headerIndex, field) => {
                    uploadModalDispatch({ 
                        type: 'SET_FIELD_MAPPING', 
                        payload: {
                            ...uploadModalState.fieldMapping,
                            [headerIndex]: field
                        }
                    });
                }}
                onPhoneFieldChange={(headerIndex, isPhone) => {
                    uploadModalDispatch({ 
                        type: 'SET_PHONE_FIELDS', 
                        payload: {
                            ...uploadModalState.phoneFields,
                            [headerIndex]: isPhone
                        }
                    });
                }}
                onCardSelect={(card) => {
                    uploadModalDispatch({ type: 'SET_SELECTED_CARD', payload: card });
                }}
                onSaveResults={handleSaveResults}
                onGoToPreviousStep={goToPreviousStep}
                onNextStep={handleNextStep}
                formValues={formValues}
                transformDataForServer={transformDataForServer}
            />
            
            {/* Диалог подтверждения */}
            <Dialog
                open={confirmDialog.isOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        {confirmDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmDialog} color="primary">
                        Отмена
                    </Button>
                    <Button onClick={handleConfirmAction} color="primary" variant="contained" autoFocus>
                        Подтвердить
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Модальное окно для редактирования базы данных */}
            <EditDatabaseModal
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                editingDatabase={editingDatabase}
                initialValues={{
                    _id: editingDatabase?._id || '',
                    url: editingDatabase?.url || '',
                    name: editingDatabase?.name || '',
                    project: editingDatabase?.query_mongo?.project || '',
                    queue: editingDatabase?.queue || editingDatabase?.queueId || '',
                    query_mongo: {
                        phones: editingDatabase?.query_mongo?.phones || [],
                        created: editingDatabase?.query_mongo?.created || [],
                        project: editingDatabase?.query_mongo?.project || ''
                    }
                }}
                isSavingEdit={isSavingEdit}
                isCheckingDatabase={isCheckingDatabase}
                isLoadingDatabase={isLoadingDatabase}
                checkResult={checkResult}
                onSaveEdit={handleSaveEdit}
                onCheckDatabase={handleCheckDatabase}
                onLoadDatabase={handleLoadDatabase}
                formValues={formValues}
                queues={queues}
            />
        </Box>
    );
};

const mapStateToProps = (state, ownProps) => ({
    formValues: getFormValues('project')(state) || {}
});

export default connect(mapStateToProps)(Databases);
